import React, { useEffect, useMemo, useState } from 'react';
import axiosInstance from '../../utils/axiosInstance';
import AuthService from '../../services/authService';
import { formatStatusLabel, deliveryStatusLabel } from '../../utils/batchStudentStatusHelpers';
import {
  formatDate,
  formatScheduleType,
  gradeBadgeStyle,
  formatMarkValue,
  isFailingMark,
  monthsSince,
  daysSince,
  subjectCellBgStyle,
  formatSubmissionDateVariance,
  formatDaysRemaining,
  daysRemainingBadgeStyle,
  groupSubjectsBySemester,
  semesterGroupBadgeStyle,
} from '../../utils/productSubmissionHelpers';

const PRINT_STYLES = `
@media print {
  @page { size: A4 landscape; margin: 14mm 10mm; }
  .print-hide { display: none !important; }
  body { background: #fff !important; }
  #product-submission-print table { page-break-inside: auto; }
  #product-submission-print tr { page-break-inside: avoid; break-inside: avoid; }
  #product-submission-print .print-course-block:not(:first-child) { page-break-before: always; break-before: page; }
  #product-submission-print .print-signoff-group { page-break-inside: avoid; break-inside: avoid; }
}
`;

function BatchMarkSheetTable({ batch, markType }) {
  const semesterGroups = groupSubjectsBySemester(batch.subjects);
  return (
    <div>
      <div className="mb-1 flex items-center justify-between">
        <p className="text-sm font-semibold text-gray-800">
          {batch.batchInternalName || batch.whatsappGroupName || batch.batchName || 'Batch'}
          <span className="ml-2 font-normal text-gray-500">({formatStatusLabel(batch.status)})</span>
          {batch.status === 'ongoing' && formatDaysRemaining(batch.batchEndingDate) && (
            <span
              className="ml-2 inline-block rounded-full px-1.5 py-0.5 text-[10px] font-bold"
              style={daysRemainingBadgeStyle(batch.batchEndingDate)}
            >
              {formatDaysRemaining(batch.batchEndingDate)}
            </span>
          )}
        </p>
        <span className="text-xs text-gray-500">
          {formatDate(batch.batchStartingDate)} – {formatDate(batch.batchEndingDate)}
        </span>
      </div>
      <table className="w-full border-collapse text-[10px]">
        <thead>
          <tr className="border border-gray-400 bg-gray-100">
            <th colSpan={5} className="border border-gray-400 px-1.5 py-1"></th>
            {semesterGroups.map((group) => (
              <th
                key={group.semester ?? 'none'}
                colSpan={group.subjects.length}
                className="border border-gray-400 px-1.5 py-1 text-center"
                style={semesterGroupBadgeStyle(group)}
              >
                Semester {group.semester ?? '—'}
                <div className="font-normal opacity-75">
                  {formatDate(group.startDate)} – {formatDate(group.endDate)}
                </div>
              </th>
            ))}
          </tr>
          <tr className="border border-gray-400 bg-gray-100">
            <th className="border border-gray-400 px-1.5 py-1 text-left">#</th>
            <th className="border border-gray-400 px-1.5 py-1 text-left">University ID</th>
            <th className="border border-gray-400 px-1.5 py-1 text-left">Student ID</th>
            <th className="border border-gray-400 px-1.5 py-1 text-left">Reg. No.</th>
            <th className="border border-gray-400 px-1.5 py-1 text-left">Name</th>
            {batch.subjects.map((subj) => {
              const overScheduled = subj.status !== 'completed'
                && typeof subj.scheduledHours === 'number'
                && subj.scheduledHours > (subj.numberOfClasses || 0) * 2;
              const deadlinePassed = !!subj.actualSubmissionDate && new Date(subj.actualSubmissionDate) < new Date();
              let headerHighlight;
              if (overScheduled) headerHighlight = { borderLeft: '2px solid #ef4444', borderRight: '2px solid #ef4444', background: '#fef2f2' };
              else if (deadlinePassed) headerHighlight = { borderLeft: '2px solid #22c55e', borderRight: '2px solid #22c55e', background: '#f0fdf4' };
              return (
              <th
                key={subj.batchSubjectId}
                className="border border-gray-400 px-1 py-1 text-center align-top"
                style={headerHighlight}
                title={subj.subjectName}
              >
                <div className="mx-auto mb-0.5 inline-block rounded px-1.5 py-0.5 font-bold text-white" style={{ background: '#1f2937' }}>
                  {subj.subjectShortName || subj.subjectName}
                </div>
                <div className="font-normal text-gray-500">{formatDate(subj.plannedSubmissionDate)}</div>
                {subj.actualSubmissionDate && (
                  <div className="mt-0.5 inline-block rounded-full px-1.5 py-0.5 font-semibold" style={{ background: '#dcfce7', color: '#047857' }}>
                    {formatDate(subj.actualSubmissionDate)}
                  </div>
                )}
                {formatSubmissionDateVariance(subj) && (
                  <div className="mt-0.5 font-semibold text-amber-700">{formatSubmissionDateVariance(subj)}</div>
                )}
                <div className="mt-0.5 font-normal text-gray-600">{subj.lecturerName || 'Not assigned'}</div>

                <table className="mx-auto mt-1 border-collapse" style={{ borderColor: '#e5e7eb' }}>
                  <tbody>
                    <tr>
                      <td className="border px-1.5 py-0.5 text-center" style={{ borderColor: '#e5e7eb' }}>
                        <div className="font-bold uppercase" style={{ color: '#0e7490', fontSize: '7px' }}>Expected</div>
                        <div className="font-bold text-gray-800">{subj.numberOfClasses != null ? `${subj.numberOfClasses * 2}h` : '—'}</div>
                        <div className="text-gray-400" style={{ fontSize: '7px' }}>{subj.numberOfClasses ?? '—'} cls</div>
                      </td>
                      <td className="border px-1.5 py-0.5 text-center" style={{ borderColor: '#e5e7eb' }}>
                        <div className="font-bold uppercase" style={{ color: overScheduled ? '#be123c' : '#6d28d9', fontSize: '7px' }}>Sched.</div>
                        <div className="font-bold text-gray-800">{typeof subj.scheduledHours === 'number' ? `${subj.scheduledHours}h` : '—'}</div>
                        <div className="text-gray-400" style={{ fontSize: '7px' }}>{subj.scheduledClasses ?? '—'} cls</div>
                      </td>
                      <td className="border px-1.5 py-0.5 text-center" style={{ borderColor: '#e5e7eb' }}>
                        <div className="font-bold uppercase" style={{ color: '#b45309', fontSize: '7px' }}>Pending</div>
                        <div className="font-bold text-gray-800">{typeof subj.pendingHours === 'number' ? `${subj.pendingHours}h` : '—'}</div>
                        <div className="text-gray-400" style={{ fontSize: '7px' }}>{subj.pendingClasses ?? '—'} cls</div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {batch.students.map((s, idx) => {
            const monthsPassed = s.universityRegisterDate ? monthsSince(s.universityRegisterDate) : null;
            return (
            <tr key={`${s.studentId}-${s.registrationNumber}`}>
              <td className="border border-gray-400 px-1.5 py-1">{idx + 1}</td>
              <td className="border border-gray-400 px-1.5 py-1">
                <div>{s.universityID || '—'}</div>
                {s.universityRegisterDate && (
                  <div className="text-gray-500">
                    {formatDate(s.universityRegisterDate)} ({monthsPassed} mo{monthsPassed === 1 ? '' : 's'})
                  </div>
                )}
              </td>
              <td className="border border-gray-400 px-1.5 py-1">{s.studentId || '—'}</td>
              <td className="border border-gray-400 px-1.5 py-1">{s.registrationNumber || '—'}</td>
              <td className="border border-gray-400 px-1.5 py-1">
                <div>{s.studentName || '—'}</div>
                <div className="text-gray-500">{deliveryStatusLabel(s.deliveryStatus)}</div>
                <div className="text-gray-500">
                  {s.attendancePercentage === null || s.attendancePercentage === undefined ? '—' : `${s.attendancePercentage}%`} att.
                </div>
                <div className="flex items-center gap-1 text-gray-500">
                  <span>Last present: {formatDate(s.lastPresentDate)}</span>
                  {s.lastPresentDate && (
                    <span className="inline-block rounded-full bg-gray-100 px-1 py-0.5 font-semibold text-gray-500">
                      {daysSince(s.lastPresentDate)}d ago
                    </span>
                  )}
                </div>
              </td>
              {batch.subjects.map((subj) => {
                const mark = s.marks ? s.marks[subj.batchSubjectId] : null;
                const rawSubmissionStatus = s.submissionStatuses ? s.submissionStatuses[subj.batchSubjectId] : null;
                // Only treat "not submitted" as NS once the actual submission
                // deadline has already passed — before that, it's just not due yet.
                const deadlinePassed = !!subj.actualSubmissionDate && new Date(subj.actualSubmissionDate) < new Date();
                const submissionStatus = deadlinePassed ? rawSubmissionStatus : null;
                const overScheduled = subj.status !== 'completed'
                  && typeof subj.scheduledHours === 'number'
                  && subj.scheduledHours > (subj.numberOfClasses || 0) * 2;
                const baseStyle = markType === 'grade'
                  ? gradeBadgeStyle(mark)
                  : isFailingMark(mark, markType)
                    ? { background: '#fee2e2', color: '#b91c1c' }
                    : subjectCellBgStyle(subj);
                return (
                  <td
                    key={subj.batchSubjectId}
                    className="border border-gray-400 px-1 py-1 text-center font-semibold"
                    style={{
                      ...baseStyle,
                      ...(overScheduled
                        ? { borderLeft: '2px solid #ef4444', borderRight: '2px solid #ef4444' }
                        : deadlinePassed
                          ? { borderLeft: '2px solid #22c55e', borderRight: '2px solid #22c55e' }
                          : {}),
                    }}
                  >
                    {formatMarkValue(mark, markType, submissionStatus)}
                  </td>
                );
              })}
            </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default function ProductSubmissionPrintView() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [courses, setCourses] = useState([]);
  const [generatedAt, setGeneratedAt] = useState(null);

  const searchParams = useMemo(() => new URLSearchParams(window.location.search), []);
  const courseIdsParam = searchParams.get('courseIds') || 'all';
  const batchStatusParam = searchParams.get('batchStatus') || 'ongoing';

  useEffect(() => {
    let isMounted = true;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const accessRes = await axiosInstance.get('/admin/me/coordinate-courses').catch(() => ({ data: {} }));
        const accessData = accessRes?.data || {};
        const isSuperAdmin = !!accessData.isSuperAdmin;
        const isManagement = String(AuthService.getUser()?.superAdmin?.staff?.staffType || '').trim().toLowerCase() === 'management';
        const accessibleCourseIds = Array.isArray(accessData.accessibleCourseIds) ? accessData.accessibleCourseIds : [];

        const accessibleParam = isSuperAdmin || isManagement
          ? 'all'
          : accessibleCourseIds.map((id) => Number(id)).filter((num) => Number.isFinite(num) && num > 0);

        const resp = await axiosInstance.get('/admin/dashboard/product-submission-report', {
          params: {
            accessibleCourses: accessibleParam,
            courseIds: courseIdsParam,
            batchStatus: batchStatusParam,
          },
        });
        if (!isMounted) return;

        const data = resp?.data || {};
        setCourses(data.courses || []);
        setGeneratedAt(data.generatedAt || new Date().toISOString());
      } catch (err) {
        if (isMounted) setError(err?.response?.data?.message || 'Failed to load product submission report');
      } finally {
        if (isMounted) setLoading(false);
      }
    })();
    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-open the print dialog once the document has rendered
  useEffect(() => {
    if (loading || error) return;
    const timer = setTimeout(() => window.print(), 400);
    return () => clearTimeout(timer);
  }, [loading, error]);

  return (
    <>
      <style>{PRINT_STYLES}</style>
      <div className="min-h-screen bg-gray-100">
        <div className="print-hide sticky top-0 z-10 flex items-center justify-between border-b border-gray-200 bg-white px-6 py-3 shadow-sm">
          <span className="text-sm font-medium text-gray-600">Print preview — Higher End Product Submission Report</span>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => window.print()}
              className="rounded-lg border border-emerald-600 bg-emerald-600 px-4 py-2 text-xs font-medium text-white hover:bg-emerald-700"
            >
              Print
            </button>
            <button
              type="button"
              onClick={() => window.close()}
              className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-xs font-medium text-gray-600 hover:bg-gray-50"
            >
              Close
            </button>
          </div>
        </div>

        <div id="product-submission-print" className="mx-auto max-w-6xl bg-white p-10 shadow-sm print:shadow-none">
          {loading ? (
            <div className="flex flex-col items-center justify-center gap-3 p-16 text-center">
              <svg className="h-9 w-9 animate-spin text-emerald-600" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              <p className="text-sm font-medium text-gray-500">Loading report for print…</p>
            </div>
          ) : error ? (
            <p className="text-center text-red-600">{error}</p>
          ) : (
            <>
              {courses.length === 0 && (
                <p className="text-center text-sm text-gray-500">No batches match the applied filters.</p>
              )}
              {courses.map((course) => {
                const courseStudentCount = course.batches.reduce((sum, b) => sum + b.students.length, 0);
                const courseBatchNames = course.batches
                  .map((b) => b.batchInternalName || b.whatsappGroupName || b.batchName || 'Batch')
                  .join(', ');
                return (
                <div key={course.courseId ?? course.courseName} className="print-course-block">
                  {/* Letterhead — repeated per course so each course prints as its own standalone report */}
                  <div className="border-b-2 border-gray-800 pb-4 text-center">
                    <h1 className="text-2xl font-black tracking-wide text-gray-900">ESOFT METRO CAMPUS BATTICALOA</h1>
                    <p className="mt-0.5 text-xs uppercase tracking-widest text-gray-500">Academic Affairs Division</p>
                  </div>

                  <div className="mt-5 text-center">
                    <span className="inline-block rounded-sm border border-gray-800 px-4 py-1 text-xs font-bold uppercase tracking-widest text-gray-800">
                      Official Notice
                    </span>
                    <h2 className="mt-3 text-xl font-bold text-gray-900">Higher End Product Submission Report</h2>
                    <p className="mt-1 text-base font-semibold text-gray-800">
                      {course.courseShortName || course.courseName || 'Unknown Course'}
                      {formatScheduleType(course.scheduleType) && ` (${formatScheduleType(course.scheduleType)})`}
                      {' — '}{course.markType === 'grade' ? 'Grade: D / M / P / R' : 'Marks: numeric'}
                    </p>
                  </div>

                  <div className="mt-5 grid grid-cols-2 gap-x-6 gap-y-1 border border-gray-300 px-4 py-3 text-xs text-gray-700">
                    <div><span className="font-semibold">Generated:</span> {generatedAt ? new Date(generatedAt).toLocaleString('en-GB') : '—'}</div>
                    <div><span className="font-semibold">Students listed:</span> {courseStudentCount}</div>
                    <div>
                      <span className="font-semibold">Batch stage:</span>{' '}
                      {batchStatusParam === 'all' ? 'All stages' : formatStatusLabel(batchStatusParam)}
                    </div>
                    <div>
                      <span className="font-semibold">Batches covered:</span> {course.batches.length}
                    </div>
                    <div className="col-span-2">
                      <span className="font-semibold">Batch names:</span> {courseBatchNames || '—'}
                    </div>
                  </div>

                  <div className="mt-6 space-y-5">
                    {course.batches.slice(0, -1).map((batch) => (
                      <BatchMarkSheetTable key={batch.batchId} batch={batch} markType={course.markType} />
                    ))}

                    {/* Last batch's table is grouped with the sign-off block so the
                        signature never ends up alone at the top of a fresh page. */}
                    <div className="print-signoff-group space-y-5">
                      {course.batches.length > 0 && (
                        <BatchMarkSheetTable
                          key={course.batches[course.batches.length - 1].batchId}
                          batch={course.batches[course.batches.length - 1]}
                          markType={course.markType}
                        />
                      )}

                      <div className="mt-8 grid grid-cols-2 gap-10 text-xs text-gray-600">
                        <div>
                          <div className="h-16"></div>
                          <div className="border-t border-gray-800 pt-1">Prepared by — Signature &amp; Date</div>
                        </div>
                        <div>
                          <div className="h-16"></div>
                          <div className="border-t border-gray-800 pt-1">Head of Department — Signature &amp; Date</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                );
              })}
            </>
          )}
        </div>
      </div>
    </>
  );
}
